/**
 * The terminal strip: the colony's system log. A one-line phosphor strip
 * pinned just above the bottom tray; tapping it unrolls a scrollback panel
 * (newest at bottom, capped at 60 lines). ask() poses a two-way question
 * with thumb-sized answer buttons directly below the strip.
 */
import { injectBaseStyles } from './win94';

export interface TickerQuestion {
  a: string;
  b: string;
  onAnswer: (choice: 0 | 1) => void;
}

const SCROLLBACK_CAP = 60;
const TERMINAL_FONT = "12px/1.45 Consolas, 'Courier New', monospace";
const TERMINAL_BORDER =
  'border:1px solid;border-color:var(--w94-dark) var(--w94-darker) var(--w94-darker) var(--w94-dark)';

const STYLE_ID = 'w94-ticker-styles';
const TICKER_CSS = `
@keyframes w94-ticker-flicker {
  0% { opacity: 0.15; }
  60% { opacity: 0.75; }
  100% { opacity: 1; }
}
.w94-ticker-flick { animation: w94-ticker-flicker 140ms steps(3, end); }
`;

function injectTickerStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = TICKER_CSS;
  document.head.appendChild(style);
}

export class Ticker {
  private readonly panel: HTMLDivElement;
  private readonly strip: HTMLDivElement;
  private readonly holder: HTMLDivElement;
  private readonly answers: HTMLDivElement;
  private current: HTMLDivElement | null = null;
  private question: TickerQuestion | null = null;
  private open = false;

  constructor(parent: HTMLElement) {
    injectBaseStyles();
    injectTickerStyles();

    const root = document.createElement('div');
    root.style.cssText = [
      'position:fixed',
      'left:8px',
      'right:8px',
      'bottom:calc(76px + env(safe-area-inset-bottom))',
      'display:flex',
      'flex-direction:column',
      'gap:4px',
      'z-index:25',
      'pointer-events:none',
      'box-sizing:border-box',
    ].join(';');

    this.panel = document.createElement('div');
    this.panel.setAttribute('role', 'log');
    this.panel.style.cssText = [
      'display:none',
      'max-height:40vh',
      'overflow-y:auto',
      'background:var(--w94-terminal-bg)',
      'color:var(--w94-terminal)',
      `font:${TERMINAL_FONT}`,
      TERMINAL_BORDER,
      'padding:6px 10px',
      'pointer-events:auto',
      'touch-action:pan-y',
      'overscroll-behavior:contain',
      '-webkit-overflow-scrolling:touch',
    ].join(';');

    this.strip = document.createElement('div');
    this.strip.style.cssText = [
      'background:var(--w94-terminal-bg)',
      'color:var(--w94-terminal)',
      `font:${TERMINAL_FONT}`,
      TERMINAL_BORDER,
      'padding:8px 10px',
      'display:flex',
      'align-items:center',
      'min-width:0',
      'cursor:pointer',
      'pointer-events:auto',
      'touch-action:manipulation',
      'user-select:none',
      '-webkit-user-select:none',
      '-webkit-tap-highlight-color:transparent',
    ].join(';');
    this.strip.addEventListener('click', () => this.toggle());

    this.holder = document.createElement('div');
    this.holder.style.cssText = 'flex:1;min-width:0;overflow:hidden;';
    this.holder.appendChild(this.makeLine(''));
    this.strip.appendChild(this.holder);

    this.answers = document.createElement('div');
    this.answers.style.cssText = 'display:none;gap:6px;pointer-events:auto;';

    root.append(this.panel, this.strip, this.answers);
    parent.appendChild(root);
  }

  /** true while a question is pending */
  get asking(): boolean {
    return this.question !== null;
  }

  /** Print a line: it becomes the current strip line and joins the scrollback. */
  print(content: string | DocumentFragment): void {
    this.pushLine(this.makeLine(content));
  }

  /**
   * Print a question line plus two answer buttons below the strip. Only one
   * question at a time — a new one replaces the old. The X dismisses without
   * answering.
   */
  ask(content: string | DocumentFragment, q: TickerQuestion): void {
    this.pushLine(this.makeLine(content));
    this.question = q;
    this.answers.replaceChildren(
      this.answerButton(q.a, 0),
      this.answerButton(q.b, 1),
      this.dismissButton(),
    );
    this.answers.style.display = 'flex';
  }

  private makeLine(content: string | DocumentFragment): HTMLDivElement {
    const el = document.createElement('div');
    el.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
    const prompt = document.createElement('span');
    prompt.textContent = '> ';
    prompt.style.opacity = '0.85';
    el.appendChild(prompt);
    if (typeof content === 'string') {
      el.appendChild(document.createTextNode(content));
    } else {
      el.appendChild(content);
    }
    return el;
  }

  private pushLine(line: HTMLDivElement): void {
    if (this.current) {
      // the superseded line joins the scrollback, newest at bottom
      this.current.style.cssText =
        'white-space:normal;word-break:break-word;padding:1px 0;';
      this.panel.appendChild(this.current);
      while (this.panel.childElementCount > SCROLLBACK_CAP) {
        this.panel.firstElementChild?.remove();
      }
      if (this.open) this.panel.scrollTop = this.panel.scrollHeight;
    }
    this.holder.replaceChildren(line);
    this.current = line;
    // brief terminal-update flicker on the strip
    this.strip.classList.remove('w94-ticker-flick');
    void this.strip.offsetWidth;
    this.strip.classList.add('w94-ticker-flick');
  }

  private toggle(): void {
    this.open = !this.open;
    this.panel.style.display = this.open ? 'block' : 'none';
    if (this.open) this.panel.scrollTop = this.panel.scrollHeight;
  }

  private answerButton(label: string, choice: 0 | 1): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'w94-btn';
    btn.textContent = label;
    btn.style.cssText = 'flex:1;min-height:44px;padding:0 10px;pointer-events:auto;';
    btn.addEventListener('click', () => {
      const q = this.question;
      this.clearQuestion();
      q?.onAnswer(choice);
    });
    return btn;
  }

  private dismissButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'w94-btn';
    btn.setAttribute('aria-label', 'dismiss');
    btn.textContent = 'X';
    btn.style.cssText = 'flex:0 0 44px;min-height:44px;pointer-events:auto;';
    btn.addEventListener('click', () => this.clearQuestion());
    return btn;
  }

  private clearQuestion(): void {
    this.question = null;
    this.answers.replaceChildren();
    this.answers.style.display = 'none';
  }
}
